// Question Card Styles
// Card deck interface for question game

import styled, { keyframes } from 'styled-components';
import { theme } from '../theme';

// Card stack container
export const QuestionCardStackContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  height: 600px;
  margin: 0.5rem auto;
  perspective: 1000px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    max-width: 90vw;
    height: 70vh;
    height: 70dvh;
    margin: 0.25rem auto;
  }
`;

// Individual question card
export const QuestionCardWrapper = styled.div<{
  $zIndex: number;
  $isTopCard: boolean;
  $offset: number;
  $rotation: number;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* Position, rotation and scale are driven by the deck's framer-motion
     layer, not here — this stays a neutral, full-size container so the two
     transform sources never fight (which previously double-rotated cards). */
  pointer-events: ${({ $isTopCard }) => ($isTopCard ? 'auto' : 'none')};
`;

export const QuestionCard = styled.div<{ $isSelected: boolean }>`
  width: 100%;
  height: 100%;
  /* Fully opaque so cards behind never show through the stack. Keeps the
     soft pink-to-cream vertical tint, just at 100% opacity. */
  background: linear-gradient(180deg,
    #ffeef2 0%,
    #ffffff 22%,
    #ffffff 55%,
    #fff6e9 100%
  );
  border: 3px solid;
  border-image: linear-gradient(135deg, #ff8c42, #ff6b9d, #ff8c42, #ffa500) 1;
  border-radius: 20px;
  padding: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: ${({ $isSelected }) =>
    $isSelected
      ? '0 24px 48px rgba(255, 140, 66, 0.35), 0 8px 16px rgba(0, 0, 0, 0.12)'
      : '0 16px 36px rgba(0, 0, 0, 0.18), 0 4px 10px rgba(0, 0, 0, 0.1)'};
  cursor: ${({ $isSelected }) => ($isSelected ? 'pointer' : 'grab')};
  position: relative;
  overflow: hidden;
  /* Smooth the shadow shift when an answer is selected */
  transition: box-shadow 0.25s ease;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 1.5rem;
    border-radius: 16px;
    border-width: 2px;
  }
`;

// Inner content column — a single "safe area" inset from the corner suit
// pips, so the question and answers never collide with them (replaces the
// old per-element magic margins). All card content lives inside this.
export const CardContent = styled.div`
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  /* Top / bottom padding clears the corner pips; sides are comfortable. */
  padding: 3.5rem 1.75rem 3.5rem;
  box-sizing: border-box;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 3rem 1.35rem 3rem;
  }
`;

// Question text — straight, centered, sits in the upper portion.
export const QuestionText = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #3c3c3c;
  line-height: 1.35;
  margin: 0 0 1.5rem;
  text-align: center;
  flex: 0 0 auto;
  overflow-wrap: break-word;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.25rem;
    margin-bottom: 1.25rem;
  }
`;

// Answer options — fill the remaining space, evenly spaced and centered.
export const AnswerOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  flex: 1;
  justify-content: center;
  min-height: 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 0.75rem;
  }
`;

// Answer button
export const AnswerButton = styled.button<{ $isSelected: boolean }>`
  background: ${({ $isSelected }) =>
    $isSelected
      ? 'linear-gradient(135deg, #ff8c42 0%, #ff6b9d 100%)'
      : '#ffffff'};
  border: 2px solid
    ${({ $isSelected }) =>
      $isSelected ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 140, 66, 0.35)'};
  border-radius: 12px;
  padding: 1rem 1.25rem;
  color: ${({ $isSelected }) => ($isSelected ? 'white' : '#3c3c3c')};
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.3;
  text-align: left;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 56px;
  display: flex;
  align-items: center;
  box-shadow: ${({ $isSelected }) =>
    $isSelected
      ? '0 4px 12px rgba(255, 140, 66, 0.3)'
      : '0 2px 4px rgba(0, 0, 0, 0.1)'};

  &:hover {
    background: ${({ $isSelected }) =>
      $isSelected
        ? 'linear-gradient(135deg, #ff8c42 0%, #ff6b9d 100%)'
        : 'rgba(255, 255, 255, 1)'};
    border-color: ${({ $isSelected }) =>
      $isSelected ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 140, 66, 0.5)'};
    transform: translateY(-2px);
    box-shadow: ${({ $isSelected }) =>
      $isSelected
        ? '0 6px 16px rgba(255, 140, 66, 0.4)'
        : '0 4px 8px rgba(0, 0, 0, 0.15)'};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.875rem 1rem;
    font-size: 0.95rem;
    min-height: 52px;
  }
`;

// Progress indicator
export const QuestionProgress = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: ${theme.colors.background.secondary};
  backdrop-filter: blur(10px);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  color: ${theme.colors.text.primary};
  font-size: 0.875rem;
  font-weight: 600;
  box-shadow: ${theme.shadows.light};
  border: 1px solid ${theme.colors.border.light};

  @media (max-width: ${theme.breakpoints.mobile}) {
    top: 0.75rem;
    right: 0.75rem;
    padding: 0.375rem 0.75rem;
    font-size: 0.8rem;
  }
`;

// Swipe hint
export const SwipeHint = styled.div<{ $show: boolean }>`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  background: ${theme.colors.background.secondary};
  backdrop-filter: blur(10px);
  padding: 0.75rem 1.5rem;
  border-radius: 20px;
  color: ${theme.colors.text.primary};
  font-size: 0.875rem;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  transition: opacity 0.3s ease;
  pointer-events: none;
  box-shadow: ${theme.shadows.light};
  border: 1px solid ${theme.colors.border.light};

  @media (max-width: ${theme.breakpoints.mobile}) {
    bottom: 0.75rem;
    padding: 0.625rem 1.25rem;
    font-size: 0.8rem;
  }
`;

// Card Suit Corner Badge (matching heart choices theme)
export const CardSuitCorner = styled.div<{ 
  $suit: string; 
  $color: string; 
  $position: 'top-left' | 'bottom-right' 
}>`
  position: absolute;
  ${props => {
    switch (props.$position) {
      case 'top-left':
        return 'top: 14px; left: 14px;';
      case 'bottom-right':
        return 'bottom: 14px; right: 14px;';
    }
  }}
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  pointer-events: none;
  /* Match heart choices theme - orange/pink gradient background */
  background: linear-gradient(135deg, rgba(255, 140, 66, 0.95) 0%, rgba(255, 107, 157, 0.95) 100%);
  padding: 5px 6px;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(255, 140, 66, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.35);

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 4px 5px;
    ${props => {
      switch (props.$position) {
        case 'top-left':
          return 'top: 12px; left: 12px;';
        case 'bottom-right':
          return 'bottom: 12px; right: 12px;';
      }
    }}
  }
`;

// Card Suit Symbol
export const CardSuitSymbol = styled.div<{ $color: string }>`
  font-size: 22px;
  line-height: 1;
  color: ${props => props.$color};
  font-weight: bold;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.5);

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 18px;
  }
`;

